import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Activity, MessageSquare, LineChart, UserCheck,
  Award, Sparkles, Check, ShieldCheck, Calendar, Bell,
} from "lucide-react";
import Layout from "../components/Layout";
import logoMark from "../assets/logo-mark.png";

const FEATURES = [
  {
    icon: Activity,
    title: "Ongoing pain logging",
    desc: "Track every body scan, flare, and shift — long after the 4-week program ends.",
  },
  {
    icon: MessageSquare,
    title: "Daily coaching",
    desc: "A short, science-backed cue every morning to keep your nervous system in the Reset State.",
  },
  {
    icon: LineChart,
    title: "Daily progress reporting",
    desc: "Visual trends across pain, mood, alignment, and adherence. See the curve, not just the day.",
  },
  {
    icon: UserCheck,
    title: "Private coaching options",
    desc: "1:1 video sessions with a Back Pain Solution coach when you need real eyes on your case.",
  },
  {
    icon: Award,
    title: "Streak badges",
    desc: "Reward the boring repetition — 7, 30, 90, 365-day badges for staying in your protocol.",
  },
  {
    icon: Bell,
    title: "Smart flare reminders",
    desc: "If a familiar trigger pattern appears, you'll get a gentle nudge before it spikes.",
  },
  {
    icon: Calendar,
    title: "Quarterly resets",
    desc: "New 30-day mini-programs every quarter — alignment, sleep, breath, mobility.",
  },
];

export default function MaintenancePlan() {
  const navigate = useNavigate();
  const [interested, setInterested] = useState(false);

  function notifyMe() {
    // Placeholder — wires to Stripe + waitlist once we're ready.
    setInterested(true);
  }

  return (
    <Layout>
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-[#9AAEC6] flex items-center gap-1 mb-4 hover:text-white transition-colors"
        data-testid="back-link"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-[#22D3EE]/25 p-6 mb-6"
          style={{
            background: "linear-gradient(160deg, rgba(34,211,238,0.16) 0%, rgba(245,158,11,0.10) 50%, rgba(10,21,48,0.92) 100%)",
            boxShadow: "0 24px 60px rgba(34,211,238,0.18), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
          data-testid="plan-hero"
        >
          <div className="absolute -top-14 -right-14 w-44 h-44 rounded-full" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.35), transparent 70%)" }} />
          <div className="absolute -bottom-16 -left-10 w-44 h-44 rounded-full" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.20), transparent 70%)" }} />

          <Sparkles size={12} className="absolute top-5 right-6 text-[#22D3EE]/70" />
          <Sparkles size={10} className="absolute top-14 right-20 text-[#F59E0B]/60" />

          <div className="relative flex items-center gap-3 mb-4">
            <img
              src={logoMark}
              alt=""
              className="w-12 h-12 rounded-2xl object-cover drop-shadow-[0_0_18px_rgba(34,211,238,0.55)]"
              draggable={false}
            />
            <div>
              <div className="bps-eyebrow text-[#22D3EE]">Reset Maintenance</div>
              <h1 className="font-display text-2xl font-light tracking-tighter text-white leading-tight">
                Stay reset. <br /> Forever.
              </h1>
            </div>
          </div>

          <p className="relative text-sm text-white/85 leading-relaxed">
            You did the hard part — you rewrote the operating system. The Maintenance plan is the
            <span className="text-white"> boring repetition</span> that makes it permanent.
          </p>

          <div className="relative mt-5 pt-4 border-t border-white/10 flex items-end justify-between">
            <div>
              <div className="bps-eyebrow text-white/55">Monthly</div>
              <div className="flex items-baseline gap-1.5 mt-1">
                <span className="font-display text-4xl font-light tracking-tighter text-white">$4.99</span>
                <span className="text-xs text-[#9AAEC6] font-mono">/ month</span>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] mt-1">
                Cancel anytime
              </div>
            </div>
            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.22em] px-2.5 py-1 rounded-full bg-[#22D3EE]/15 border border-[#22D3EE]/30 text-[#22D3EE]">
              <ShieldCheck size={11} /> 7-day trial
            </span>
          </div>
        </div>

        {/* Feature list */}
        <div className="bps-eyebrow mb-3">What's included</div>
        <ul className="space-y-3" data-testid="feature-list">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <li
                key={f.title}
                className="bps-card p-4 flex items-start gap-4"
                data-testid={`feature-${i + 1}`}
              >
                <div className="w-11 h-11 rounded-2xl bg-[#22D3EE]/12 border border-[#22D3EE]/25 flex items-center justify-center shrink-0">
                  <Icon size={18} className="text-[#22D3EE]" strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base text-white leading-snug">{f.title}</div>
                  <p className="text-xs text-[#9AAEC6] mt-1 leading-relaxed">{f.desc}</p>
                </div>
                <Check size={14} className="text-[#22D3EE] mt-1.5 shrink-0" />
              </li>
            );
          })}
        </ul>

        {/* CTA */}
        <div className="mt-7 space-y-3" data-testid="cta-section">
          {!interested ? (
            <button
              type="button"
              onClick={notifyMe}
              data-testid="opt-in-button"
              className="w-full py-4 rounded-full font-semibold text-[#03101F] hover:-translate-y-0.5 transition-all"
              style={{
                background: "linear-gradient(180deg, #FCD34D, #F59E0B)",
                boxShadow: "0 0 24px rgba(245,158,11,0.45), inset 0 1px 0 rgba(255,255,255,0.5)",
              }}
            >
              Start 7-day trial · $4.99/mo
            </button>
          ) : (
            <div className="rounded-2xl border border-[#22D3EE]/30 bg-[#22D3EE]/[0.08] p-4 flex items-start gap-3" data-testid="opt-in-success">
              <Check size={18} className="text-[#22D3EE] mt-0.5 shrink-0" />
              <div>
                <div className="font-display text-base text-white">You're on the list.</div>
                <p className="text-xs text-[#9AAEC6] mt-1 leading-relaxed">
                  We'll email you the moment the Maintenance plan opens for sign-ups.
                </p>
              </div>
            </div>
          )}

          <p className="text-[11px] text-[#7C8FA8] text-center font-mono uppercase tracking-[0.18em]">
            Includes everything in the original 4-week course · Cancel in one tap
          </p>
        </div>

        {/* Soft re-entry */}
        <button
          type="button"
          onClick={() => navigate("/home")}
          data-testid="not-now-link"
          className="mt-6 mb-2 w-full py-3 text-sm text-[#9AAEC6] hover:text-white transition-colors"
        >
          Not right now — return home
        </button>
      </motion.div>
    </Layout>
  );
}
